/* app/lazyScreen.js — ленивый экран раздела (route-level code splitting, kitchen-ux follow-up 30).

   lazyScreen(load, name) — React.lazy над ИМЕНОВАННЫМ экспортом модуля раздела (экраны
   экспортируют компонент по имени, default-экспортов в проекте нет) + preload(): тот же промис,
   что отдаст React.lazy, поэтому предзагрузка (адрес старта в main.jsx, наведение на ссылку) и
   рендер не качают чанк дважды. screenId — имя экрана: по нему ScreenBoundary даёт каждому
   разделу свою границу.

   Сбой ЗАГРУЗКИ чанка (сеть, чанк прежней сборки после выката — nginx отдаёт вместо него
   index.html, сбой CSS-зависимости у Vite-прелоада) помечается chunkLoadError: его и только его
   ловит ScreenBoundary. Ошибка ВЫПОЛНЕНИЯ модуля (TDZ при цикле чанков, throw на верхнем уровне)
   — ошибка кода: она уходит выше, как и до разбиения, а не выдаётся за «вышла новая версия».
   После сбоя промис предзагрузки сбрасывается, и следующее наведение пробует заново. Сам
   React.lazy отказ запоминает навсегда (и браузер — упавший import тоже), поэтому раздел после
   сбоя восстанавливает только перезагрузка страницы — её и делает ScreenBoundary. */

import React from 'react';

// тексты, которыми браузеры и Vite сообщают именно о сбое загрузки модуля (Chrome, Firefox, Safari,
// Vite-прелоад CSS); всё остальное — ошибка выполнения
const LOAD_FAILURE = /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/i;

function isLoadFailure(err) {
  const message = err && typeof err === 'object' ? err.message : err;
  return LOAD_FAILURE.test(String(message || ''));
}

function markChunkError(err) {
  const e = err instanceof Error ? err : new Error(String(err));
  try { e.chunkLoadError = true; } catch (_) { /* замороженный объект ошибки — оставляем как есть */ }
  return e;
}

function isChunkLoadError(err) {
  return !!(err && err.chunkLoadError === true);
}

// один промис на все вызовы, пока загрузка не упала; упала — следующий вызов грузит заново
function loaderOf(load) {
  let pending = null;
  return () => {
    if (!pending) {
      pending = load().catch((err) => {
        pending = null;
        throw isLoadFailure(err) ? markChunkError(err) : err;
      });
    }
    return pending;
  };
}

// экспорт переименовали, а реестр забыли — это ошибка кода, а не загрузки: не маскируем её
// под «обновите страницу»
function pickExport(mod, name) {
  if (!mod || mod[name] == null) throw new Error('lazyScreen: модуль раздела не экспортирует ' + name);
  return { default: mod[name] };
}

function lazyScreen(load, name) {
  const preload = loaderOf(load);
  const Screen = React.lazy(() => preload().then((mod) => pickExport(mod, name)));
  // предзагрузка «на всякий случай» молчит: сбой проявится (и обработается) при рендере
  Screen.preload = () => preload().then(() => true, () => false);
  Screen.screenId = name;
  Screen.displayName = 'Lazy(' + name + ')';
  return Screen;
}

export { lazyScreen, isChunkLoadError, isLoadFailure, markChunkError, loaderOf, pickExport };
